// ============================================
// Sector Store - BinnaclePro
// Global state management for sectors using Angular Signals
// Similar to AWS Region selector pattern
// ============================================

import { computed, Injectable, signal } from '@angular/core';
import { GLOBAL_SECTOR, Sector, SECTOR_STORAGE_KEY } from '../models/sector.model';

/**
 * Estado global para el manejo de sectores
 *
 * Este store es inyectado a nivel root, lo que significa que:
 * - El estado persiste mientras la app esté abierta
 * - Todos los componentes comparten el mismo estado
 * - Los cambios de sector disparan actualizaciones automáticas
 *
 * Patrón similar a AWS Region:
 * - selectedSector = región actual
 * - sectors = lista de regiones disponibles
 * - Cambiar de sector = cambiar de región (recarga datos)
 */
@Injectable({
  providedIn: 'root'
})
export class SectorStore {
  // ============================================
  // Private State (signals internos)
  // ============================================

  private _sectors = signal<Sector[]>([]);
  private _selectedSector = signal<Sector>(this.getStoredSector());
  private _loading = signal(false);
  private _initialized = signal(false);

  /**
   * Versión del estado que se incrementa cada vez que cambia el sector.
   * Los componentes pueden usar esto para saber cuándo recargar datos.
   */
  private _version = signal(0);

  // ============================================
  // Public Readonly Signals (acceso externo)
  // ============================================

  /** Lista de sectores disponibles */
  readonly sectors = this._sectors.asReadonly();

  /** Sector actualmente seleccionado */
  readonly selectedSector = this._selectedSector.asReadonly();

  /** Indica si se están cargando los sectores */
  readonly loading = this._loading.asReadonly();

  /** Indica si el store ha sido inicializado */
  readonly initialized = this._initialized.asReadonly();

  /** Versión del estado (útil para efectos de recarga) */
  readonly version = this._version.asReadonly();

  // ============================================
  // Computed Signals
  // ============================================

  /** ID del sector seleccionado (0 = global) */
  readonly selectedSectorId = computed(() => this._selectedSector().id);

  /** Nombre del sector seleccionado */
  readonly selectedSectorName = computed(() => this._selectedSector().name);

  /** Indica si se está en modo global (todos los sectores) */
  readonly isGlobalMode = computed(() => this._selectedSector().id === 0);

  /** Indica si hay sectores cargados */
  readonly hasSectors = computed(() => this._sectors().length > 0);

  // ============================================
  // Métodos Públicos
  // ============================================

  /**
   * Inicializa el store con la lista de sectores
   * Debe llamarse después del login exitoso
   */
  setSectors(sectors: Sector[]): void {
    // Agregar la opción "Global" al principio
    const sectorsWithGlobal = [GLOBAL_SECTOR, ...sectors];
    this._sectors.set(sectorsWithGlobal);
    this._initialized.set(true);

    // Si el sector almacenado ya no existe, usar Global
    const stored = this._selectedSector();
    const exists = sectorsWithGlobal.some(s => s.id === stored.id);
    if (!exists) {
      this.selectSector(GLOBAL_SECTOR);
    }
  }

  /**
   * Cambia el sector seleccionado
   * Esto dispara una recarga de datos en toda la aplicación
   */
  selectSector(sector: Sector): void {
    const current = this._selectedSector();

    // Solo actualizar si el sector es diferente
    if (current.id !== sector.id) {
      this._selectedSector.set(sector);
      this.storeSector(sector);

      // Incrementar versión para notificar cambio a los componentes
      this._version.update(v => v + 1);

    }
  }

  /**
   * Selecciona un sector por su ID
   */
  selectSectorById(id: number): void {
    const sector = this._sectors().find(s => s.id === id);
    if (sector) {
      this.selectSector(sector);
    } else if (id === 0) {
      this.selectSector(GLOBAL_SECTOR);
    }
  }

  /**
   * Cambia al modo global (todos los sectores)
   */
  setGlobalMode(): void {
    this.selectSector(GLOBAL_SECTOR);
  }

  /**
   * Establece el estado de carga
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  /**
   * Resetea el store al estado inicial
   * Debe llamarse en logout
   */
  reset(): void {
    this._sectors.set([]);
    this._selectedSector.set(GLOBAL_SECTOR);
    this._initialized.set(false);
    this._version.set(0);
    localStorage.removeItem(SECTOR_STORAGE_KEY);
  }

  // ============================================
  // Métodos Privados (persistencia)
  // ============================================

  /**
   * Obtiene el sector almacenado en localStorage
   */
  private getStoredSector(): Sector {
    try {
      const stored = localStorage.getItem(SECTOR_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('[SectorStore] Error leyendo sector almacenado:', error);
    }
    return GLOBAL_SECTOR;
  }

  /**
   * Guarda el sector seleccionado en localStorage
   */
  private storeSector(sector: Sector): void {
    try {
      localStorage.setItem(SECTOR_STORAGE_KEY, JSON.stringify(sector));
    } catch (error) {
      console.warn('[SectorStore] Error guardando sector:', error);
    }
  }
}
